/**
 * Unified Booking Theme System
 *
 * One theme definition drives ALL three surfaces:
 *   1. Link-in-bio page
 *   2. Booking flow (service, date, details steps)
 *   3. Confirmation pass (the morphing booking card)
 *
 * ── Adding a new theme ──────────────────────────────────────────
 *
 * 1. Add an entry to BOOKING_THEMES below with ALL required fields:
 *    - Pass tokens: bg, bgSoft, fg, accent, accentSoft, border, borderStrong
 *    - Typography: display, displayWeight, displayItalic, ui
 *    - Font: googleFontUrl (use one of the FONT_* constants, or add a new one)
 *    - Scallop: passScallop (color that punches through the pass perforation —
 *      use FLOW_DEFAULTS.bg for light-flow themes, or flowBg for dark themes)
 *    - Flow accent: flowAccent, flowAccentSoft, flowAccentBorder
 *
 * 2. For light-flow themes (off-white background): no dark-flow overrides needed.
 *    For dark-flow themes: set dark: true and provide ALL flow* overrides
 *    (flowBg, flowSurface, flowInk, flowInkSoft, flowInkFaint, flowHairline,
 *    flowSelBg, flowSelFg). See `emerald` for the pattern.
 *
 * 3. Optional link-in-bio overrides: libAvatar, libCta, libCtaFg, etc.
 *    If omitted, they derive from the pass tokens.
 *
 * 4. Add the theme key to STANDARD_FLOW_THEME_KEYS in
 *    BookingPageSettings.tsx if it should appear in the selector.
 *
 * 5. If using a new Google Font family, add a FONT_* constant URL above
 *    the registry. loadThemeFonts() will load it dynamically.
 */

// ─── Theme interface ───────────────────────────────────────────────

export interface BookingTheme {
  key: string;
  name: string;

  // ── Pass / card tokens (confirmation card background) ──
  bg: string;           // Pass background
  bgSoft: string;       // Subtle radial wash overlay
  fg: string;           // Pass foreground text
  accent: string;       // Accent color (gold, lime, lavender, etc.)
  accentSoft: string;   // Very low-opacity accent bg
  border: string;       // Low-opacity border
  borderStrong: string; // Slightly stronger border

  // ── Typography ──
  display: string;      // Display font-family (brand identity)
  displayWeight: number;
  displayItalic: boolean;
  ui: string;           // UI font-family (Inter Tight default)
  googleFontUrl?: string; // Google Fonts CSS URL — loaded dynamically when theme is active

  // ── Confirmation pass ──
  passScallop: string;  // Perforation scallop circle color (should punch through to the scene bg)

  // ── Flow tokens (booking step screens) ──
  // These default to light. Override them for dark-themed flows.
  flowAccent: string;       // Accent on flow screens (selected state)
  flowAccentSoft: string;   // Low-opacity accent background
  flowAccentBorder: string; // Accent border

  // Optional dark-flow overrides (set `dark: true` to enable)
  dark?: boolean;
  flowBg?: string;          // Flow background (default: FLOW_DEFAULTS.bg)
  flowSurface?: string;     // Elevated surface (default: FLOW_DEFAULTS.surface)
  flowInk?: string;         // Primary text (default: #1c160f)
  flowInkSoft?: string;     // Secondary text
  flowInkFaint?: string;    // Faint text / labels
  flowHairline?: string;    // Hairline dividers
  flowSelBg?: string;       // Selected chip/day background
  flowSelFg?: string;       // Selected chip/day text

  // ── Link-in-bio overrides (optional — defaults derived from pass tokens) ──
  libAvatar?: string;       // Avatar background color
  libAvatarFg?: string;     // Avatar initial color
  libAvatarRing?: string;   // Avatar ring color
  libRingHalo?: string;     // Soft halo behind ring
  libCta?: string;          // "Book appointment" button bg
  libCtaFg?: string;        // "Book appointment" button text
  libFiggyColor?: string;   // "Powered by Figgy" mark color

  // ── Business identity (used in flow prototype, not stored in DB) ──
  tagline?: string;
  statusBarDark?: boolean;
}

// ─── Helper: type assertion for theme entries ─────────────────────

function theme(t: BookingTheme): BookingTheme {
  return t;
}

// ─── Default flow token values (light) ────────────────────────────

// Shared spring easing used across flow transitions and chips
export const FLOW_SPRING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const FLOW_DEFAULTS = {
  // Light off-white rather than the previous warm cream (#F4F1EB). Every theme
  // except the dark-flow one (emerald) renders on this, so it is effectively
  // the default page colour for published booking pages.
  bg: '#FBFAF7',
  // Cards must stay lighter than the page to read as elevated; with a near-white
  // bg that means white, carrying the separation on cardShadow and hairline.
  surface: '#FFFFFF',
  ink: '#1c160f',
  inkSoft: 'rgba(28,18,12,0.62)',
  inkFaint: 'rgba(28,18,12,0.40)',
  hairline: 'rgba(28,18,12,0.10)',
  selBg: '#1c160f',
  selFg: '#F5EDE0',
  cardShadow: '0 1px 2px rgba(28,18,12,0.05), 0 6px 16px rgba(28,18,12,0.06)',
} as const;

// Width of the design's phone frame — the flow chrome and content are
// constrained to one centered column of this width on wide screens.
export const FLOW_COLUMN_MAX = 402;

// ─── Google Font URLs (shared across themes that use the same font) ──

const FONT_INTER_TIGHT = 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@300;400;450;500;600;700&display=swap';
const FONT_CORMORANT = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap';
const FONT_SPACE_GROTESK = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap';
const FONT_BRICOLAGE = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,500;12..96,600;12..96,700&display=swap';
const FONT_INSTRUMENT_SERIF = 'https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap';

// ─── Theme registry ───────────────────────────────────────────────

export const BOOKING_THEMES: Record<string, BookingTheme> = {
  // ── Figgy Pop (General Use) ─────────────────────────────────────
  figgy_purple: theme({
    key: 'figgy_purple',
    name: 'Figgy Pop',
    bg: '#5956F6',
    bgSoft: '#6C69F7',
    fg: '#F5EDE0',
    accent: '#F5EDE0',
    accentSoft: 'rgba(245,237,224,0.10)',
    border: 'rgba(245,237,224,0.22)',
    borderStrong: 'rgba(245,237,224,0.38)',
    // Satoshi is the Figgy brand face and is loaded app-wide from Fontshare in
    // index.html, so it needs no googleFontUrl entry. Inter Tight stays as the
    // UI face (and is still fetched below for it).
    display: '"Satoshi", "Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    statusBarDark: true,
    flowAccent: '#5956F6',
    flowAccentSoft: 'rgba(89,86,246,0.06)',
    flowAccentBorder: 'rgba(89,86,246,0.20)',
  }),

  // ── Espresso (Warm editorial serif) ─────────────────────────────
  cognac: theme({
    key: 'cognac',
    name: 'Espresso',
    bg: '#5C3A2C',
    bgSoft: '#6B4636',
    fg: '#F3E8DA',
    accent: '#CBA06A',
    accentSoft: 'rgba(243,232,218,0.10)',
    border: 'rgba(243,232,218,0.20)',
    borderStrong: 'rgba(243,232,218,0.36)',
    display: '"Cormorant Garamond", "Times New Roman", serif',
    displayWeight: 500,
    displayItalic: true,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_CORMORANT,
    passScallop: FLOW_DEFAULTS.bg,
    statusBarDark: true,
    flowAccent: '#5C3A2C',
    flowAccentSoft: 'rgba(92,58,44,0.06)',
    flowAccentBorder: 'rgba(92,58,44,0.20)',
  }),

  // ── After Dark (Sharp monochrome) ───────────────────────────────
  modern_black: theme({
    key: 'modern_black',
    name: 'After Dark',
    bg: '#141414',
    bgSoft: '#1c1c1c',
    fg: '#E8E4DC',
    accent: '#C9A961',
    accentSoft: 'rgba(232,228,220,0.06)',
    border: 'rgba(232,228,220,0.15)',
    borderStrong: 'rgba(232,228,220,0.30)',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    statusBarDark: true,
    flowAccent: '#141414',
    flowAccentSoft: 'rgba(20,20,20,0.05)',
    flowAccentBorder: 'rgba(20,20,20,0.18)',
  }),

  // ── Jam (Phantom Wallet / crypto-fintech vibe) ──────────────────
  plum: theme({
    key: 'plum',
    name: 'Jam',
    bg: '#1A1530',
    bgSoft: '#251E40',
    fg: '#EAE4FF',
    accent: '#AB9FF2',
    accentSoft: 'rgba(234,228,255,0.06)',
    border: 'rgba(234,228,255,0.14)',
    borderStrong: 'rgba(234,228,255,0.30)',
    display: '"Space Grotesk", "Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_SPACE_GROTESK,
    passScallop: FLOW_DEFAULTS.bg,
    statusBarDark: true,
    flowAccent: '#6A40A8',
    flowAccentSoft: 'rgba(106,64,168,0.07)',
    flowAccentBorder: 'rgba(106,64,168,0.22)',
    libCta: '#1A1530',
    libCtaFg: '#EAE4FF',
  }),

  // ── Lucky Girl (Dark-flow design system — Linktree vibe) ────────
  emerald: theme({
    key: 'emerald',
    name: 'Lucky Girl',
    bg: '#15402E',
    bgSoft: '#1C4F39',
    fg: '#EFF4E2',
    accent: '#C7F24E',
    accentSoft: 'rgba(239,244,226,0.08)',
    border: 'rgba(239,244,226,0.18)',
    borderStrong: 'rgba(239,244,226,0.34)',
    display: '"Bricolage Grotesque", "Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_BRICOLAGE,
    passScallop: '#102B1F',
    statusBarDark: true,

    // Dark-flow: the SAME tokens drive the link-in-bio AND the booking flow
    dark: true,
    flowBg: '#102B1F',
    flowSurface: '#1B4331',
    flowInk: '#EFF4E2',
    flowInkSoft: 'rgba(239,244,226,0.66)',
    flowInkFaint: 'rgba(239,244,226,0.45)',
    flowHairline: 'rgba(239,244,226,0.16)',
    flowSelBg: '#C7F24E',
    flowSelFg: '#15402E',
    flowAccent: '#C7F24E',
    flowAccentSoft: 'rgba(199,242,78,0.14)',
    flowAccentBorder: 'rgba(199,242,78,0.5)',

    // Link-in-bio: mint avatar + button on dark background
    libAvatar: '#C7F24E',
    libAvatarFg: '#15402E',
    libAvatarRing: 'rgba(199,242,78,0.45)',
    libRingHalo: 'rgba(239,244,226,0.10)',
    libCta: '#C7F24E',
    libCtaFg: '#15402E',
    libFiggyColor: '#C7F24E',
  }),

  // ─── Legacy themes (backward-compat with existing booking cards) ──

  midnight_luxe: theme({
    key: 'midnight_luxe',
    name: 'Night Mode',
    bg: '#0f0f1a',
    bgSoft: '#161625',
    fg: '#ffffff',
    accent: '#3b82f6',
    accentSoft: 'rgba(59,130,246,0.10)',
    border: '#1f2937',
    borderStrong: '#374151',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    statusBarDark: true,
    flowAccent: '#3b82f6',
    flowAccentSoft: 'rgba(59,130,246,0.06)',
    flowAccentBorder: 'rgba(59,130,246,0.20)',
  }),

  clean_mono: theme({
    key: 'clean_mono',
    name: 'Clean Slate',
    bg: '#ffffff',
    bgSoft: '#f8f8f8',
    fg: '#09090b',
    accent: '#18181b',
    accentSoft: 'rgba(24,24,27,0.05)',
    border: '#e4e4e7',
    borderStrong: '#d4d4d8',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    flowAccent: '#18181b',
    flowAccentSoft: 'rgba(24,24,27,0.05)',
    flowAccentBorder: 'rgba(24,24,27,0.18)',
  }),

  warm_gold: theme({
    key: 'warm_gold',
    name: 'Honey',
    bg: '#fefce8',
    bgSoft: '#fef9c3',
    fg: '#713f12',
    accent: '#ca8a04',
    accentSoft: 'rgba(202,138,4,0.08)',
    border: '#fde047',
    borderStrong: '#facc15',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    flowAccent: '#ca8a04',
    flowAccentSoft: 'rgba(202,138,4,0.06)',
    flowAccentBorder: 'rgba(202,138,4,0.20)',
  }),

  soft_stone: theme({
    key: 'soft_stone',
    name: 'Soft Launch',
    bg: '#fafaf9',
    bgSoft: '#f5f5f4',
    fg: '#1c1917',
    accent: '#78716c',
    accentSoft: 'rgba(120,113,108,0.06)',
    border: '#e7e5e4',
    borderStrong: '#d6d3d1',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    flowAccent: '#78716c',
    flowAccentSoft: 'rgba(120,113,108,0.06)',
    flowAccentBorder: 'rgba(120,113,108,0.20)',
  }),

  electric_edge: theme({
    key: 'electric_edge',
    name: 'Static',
    bg: '#0a0a0f',
    bgSoft: '#111118',
    fg: '#f0f9ff',
    accent: '#06b6d4',
    accentSoft: 'rgba(6,182,212,0.08)',
    border: '#164e63',
    borderStrong: '#0e7490',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    statusBarDark: true,
    flowAccent: '#06b6d4',
    flowAccentSoft: 'rgba(6,182,212,0.06)',
    flowAccentBorder: 'rgba(6,182,212,0.20)',
  }),

  gradient: theme({
    key: 'gradient',
    name: 'Aura',
    bg: '#FAFAFA',
    bgSoft: '#f5f5f5',
    fg: '#111111',
    accent: '#111111',
    accentSoft: 'rgba(0,0,0,0.04)',
    border: 'rgba(0,0,0,0.06)',
    borderStrong: 'rgba(0,0,0,0.12)',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    flowAccent: '#111111',
    flowAccentSoft: 'rgba(0,0,0,0.04)',
    flowAccentBorder: 'rgba(0,0,0,0.12)',
  }),

  rich_gradient: theme({
    key: 'rich_gradient',
    name: 'Hyperpop',
    bg: '#FFF0F8',
    bgSoft: '#fce7f3',
    fg: '#111111',
    accent: '#111111',
    accentSoft: 'rgba(0,0,0,0.04)',
    border: 'rgba(0,0,0,0.06)',
    borderStrong: 'rgba(0,0,0,0.12)',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    flowAccent: '#111111',
    flowAccentSoft: 'rgba(0,0,0,0.04)',
    flowAccentBorder: 'rgba(0,0,0,0.12)',
  }),

  sky_ripple: theme({
    key: 'sky_ripple',
    name: 'Sky Ripple',
    bg: '#E2ECF6',
    bgSoft: '#d1e3f3',
    fg: '#1e3a5f',
    accent: '#C2E4FF',
    accentSoft: 'rgba(194,228,255,0.20)',
    border: '#B0D4F5',
    borderStrong: '#7bbae8',
    display: '"Inter Tight", system-ui, sans-serif',
    displayWeight: 600,
    displayItalic: false,
    ui: '"Inter Tight", system-ui, sans-serif',
    googleFontUrl: FONT_INTER_TIGHT,
    passScallop: FLOW_DEFAULTS.bg,
    flowAccent: '#1e3a5f',
    flowAccentSoft: 'rgba(30,58,95,0.06)',
    flowAccentBorder: 'rgba(30,58,95,0.20)',
  }),
};

// ─── Lookup ───────────────────────────────────────────────────────

export function getBookingTheme(themeKey: string): BookingTheme {
  return BOOKING_THEMES[themeKey] || BOOKING_THEMES.figgy_purple;
}

// ─── Dynamic font loader ─────────────────────────────────────────
// Loads the theme's Google Font CSS only when the theme is active.
// Idempotent — each URL is loaded at most once per page lifecycle.
// Also loads Instrument Serif (used by ConfirmationPass TrayChrome).

const loadedFontUrls = new Set<string>();

export function loadThemeFonts(theme: BookingTheme): void {
  if (typeof document === 'undefined') return;

  const urls = [
    FONT_INTER_TIGHT, // UI font for every theme (body text, buttons, labels)
    theme.googleFontUrl,
    FONT_INSTRUMENT_SERIF, // always needed for "You're booked in." headline
  ].filter((u): u is string => !!u);

  for (const url of urls) {
    if (loadedFontUrls.has(url)) continue;
    loadedFontUrls.add(url);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }
}

// ─── CSS variable helper ──────────────────────────────────────────
// Sets CSS custom properties on a container so all children inherit
// the theme's flow tokens. Used by the flow scaffold.

export function flowThemeVars(theme: BookingTheme): Record<string, string> {
  const v: Record<string, string> = {};
  if (theme.flowBg) v['--f-bg'] = theme.flowBg;
  if (theme.flowSurface) v['--f-surface'] = theme.flowSurface;
  if (theme.flowInk) v['--f-ink'] = theme.flowInk;
  if (theme.flowInkSoft) v['--f-ink-soft'] = theme.flowInkSoft;
  if (theme.flowInkFaint) v['--f-ink-faint'] = theme.flowInkFaint;
  if (theme.flowHairline) v['--f-hairline'] = theme.flowHairline;
  if (theme.flowSelBg) v['--f-sel-bg'] = theme.flowSelBg;
  if (theme.flowSelFg) v['--f-sel-fg'] = theme.flowSelFg;
  // Accent tokens (selected cards, "Most booked", today dot)
  v['--f-accent'] = theme.flowAccent;
  v['--f-accent-soft'] = theme.flowAccentSoft;
  v['--f-accent-border'] = theme.flowAccentBorder;
  // Display font tokens so step components can render brand typography
  // without receiving the theme object as a prop
  v['--f-display'] = theme.display;
  v['--f-display-weight'] = String(theme.displayWeight);
  v['--f-display-style'] = theme.displayItalic ? 'italic' : 'normal';
  return v;
}

// ─── Hex-to-HSL utility ───────────────────────────────────────────
// Tailwind uses HSL triplets (e.g. "240 5.9% 10%") for its CSS variables.
// This converts hex colors into that format so we can scope-override
// Tailwind's semantic tokens within the booking flow container.

function computeHSL(r: number, g: number, b: number): string {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let hue = 0, sat = 0;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn: hue = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6; break;
      case gn: hue = ((bn - rn) / d + 2) / 6; break;
      case bn: hue = ((rn - gn) / d + 4) / 6; break;
    }
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

function hexToHSL(hex: string): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return computeHSL((n >> 16) & 255, (n >> 8) & 255, n & 255);
}

function hexToRGB(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbaToRGB(rgba: string): { r: number; g: number; b: number } | null {
  const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return null;
  return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
}

function rgbToHSL(r: number, g: number, b: number): string {
  return computeHSL(r, g, b);
}

// Parse rgba() to extract opacity for soft/faint colors
function rgbaToHSLWithAlpha(rgba: string): { hsl: string; alpha: number } | null {
  const match = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)/);
  if (!match) return null;
  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);
  const a = match[4] ? parseFloat(match[4]) : 1;
  return { hsl: computeHSL(r, g, b), alpha: a };
}

// ─── Scoped Tailwind variable overrides ───────────────────────────
// Maps BookingTheme tokens to Tailwind's HSL-based CSS custom properties.
// Apply these on the flow container so all child Shadcn/Tailwind components
// (Card, Calendar, Button, Input, etc.) automatically inherit the theme.
//
// This is the key integration: the existing real step components don't need
// any code changes — they read from Tailwind's CSS variables, and we
// override those variables within the flow scope.

export function flowTailwindOverrides(theme: BookingTheme): Record<string, string> {
  const bg = theme.flowBg || FLOW_DEFAULTS.bg;
  const surface = theme.flowSurface || FLOW_DEFAULTS.surface;
  const ink = theme.flowInk || FLOW_DEFAULTS.ink;
  const inkSoft = theme.flowInkSoft || FLOW_DEFAULTS.inkSoft;
  const accent = theme.flowAccent;
  const hairline = theme.flowHairline || FLOW_DEFAULTS.hairline;

  const bgHSL = hexToHSL(bg);
  const surfaceHSL = hexToHSL(surface);
  const inkHSL = hexToHSL(ink);
  const accentHSL = hexToHSL(accent);

  // Composite rgba colors against the background to get the actual perceived HSL.
  // e.g., rgba(28,18,12,0.10) on the off-white page bg produces a very light warm gray,
  // not a dark color. Tailwind uses opaque HSL, so we need the composited result.
  const bgRGB = hexToRGB(bg);
  const compositeRgba = (rgbaStr: string): string => {
    const parsed = rgbaToHSLWithAlpha(rgbaStr);
    if (!parsed) return bgHSL;
    const rgbaRGB = rgbaToRGB(rgbaStr);
    if (!rgbaRGB) return parsed.hsl;
    // Alpha-composite over background
    const a = parsed.alpha;
    const cr = Math.round(rgbaRGB.r * a + bgRGB.r * (1 - a));
    const cg = Math.round(rgbaRGB.g * a + bgRGB.g * (1 - a));
    const cb = Math.round(rgbaRGB.b * a + bgRGB.b * (1 - a));
    return rgbToHSL(cr, cg, cb);
  };

  const mutedFgHSL = compositeRgba(inkSoft);
  const borderHSL = compositeRgba(hairline);

  // For muted/secondary backgrounds, slightly shift from the surface
  const mutedHSL = surfaceHSL;

  // Selected state / primary accent
  const selBg = theme.flowSelBg || FLOW_DEFAULTS.selBg;
  const selFg = theme.flowSelFg || FLOW_DEFAULTS.selFg;
  const selBgHSL = hexToHSL(selBg);
  const selFgHSL = hexToHSL(selFg);

  // Accent background (very soft version of accent, composited)
  const accentSoftHSL = compositeRgba(theme.flowAccentSoft);

  return {
    // Core backgrounds
    '--background': bgHSL,
    '--foreground': inkHSL,

    // Card surfaces
    '--card': surfaceHSL,
    '--card-foreground': inkHSL,

    // Popover (dropdowns, date pickers)
    '--popover': surfaceHSL,
    '--popover-foreground': inkHSL,

    // Primary accent (selected states, checkmarks, rings)
    '--primary': accentHSL,
    '--primary-foreground': selFgHSL,

    // Secondary / muted
    '--secondary': mutedHSL,
    '--secondary-foreground': inkHSL,
    '--muted': mutedHSL,
    '--muted-foreground': mutedFgHSL,

    // Accent (calendar selected, hover states)
    '--accent': accentSoftHSL,
    '--accent-foreground': inkHSL,

    // Borders and inputs
    '--border': borderHSL,
    '--input': borderHSL,
    '--ring': accentHSL,
  };
}
